/**
 * Converter for multi-server configuration format
 *
 * Handles inputs like:
 * {
 *   "playwright": {
 *     "command": "npx",
 *     "args": ["@playwright/mcp@latest"]
 *   },
 *   "github": {
 *     "command": "npx",
 *     "args": ["-y", "@modelcontextprotocol/server-github"]
 *   }
 * }
 *
 * This is the inner content of mcpServers without the wrapper.
 */

import type { ConverterEntry, McpServerConfig, ParsedMcpServer } from '../types';
import { isServerNameConfigMapping, isValidServerConfig, normalizeConfig } from './utils';

/**
 * Detects if input is a multi-server name -> config mapping
 */
const detect = (input: unknown): boolean => {
  if (typeof input !== 'object' || input === null) {
    return false;
  }

  const obj = input as Record<string, unknown>;

  // Should NOT have mcpServers key (that's wrapped format)
  if ('mcpServers' in obj) {
    return false;
  }

  // Should NOT be a single server config
  if (isValidServerConfig(input)) {
    return false;
  }

  // Should be a mapping of names to configs
  return isServerNameConfigMapping(input);
};

/**
 * Converts multi-server format to ParsedMcpServer array
 */
const convert = (input: unknown): ParsedMcpServer[] => {
  const obj = input as Record<string, McpServerConfig>;
  return Object.entries(obj)
    .filter(([, config]) => isValidServerConfig(config))
    .map(([name, config]) => ({
      name,
      config: normalizeConfig(config),
    }));
};

export const multiServerConverter: ConverterEntry = {
  format: 'multi-server',
  detect,
  convert,
  priority: 75, // Higher than single, lower than wrapped
};
